'use client';

import React from 'react';
import Link from 'next/link';

import Icon from '@/components/ui/AppIcon';

const PricingPhilosophySection = () => {
  return (
    <section className="relative overflow-hidden py-20 lg:py-32 bg-[radial-gradient(circle_at_top_left,rgba(44,120,251,0.10),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(62,224,190,0.18),transparent_34%),linear-gradient(180deg,#f4f9ff_0%,#ffffff_46%,#edfdf8_100%)]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[-6rem] top-10 h-72 w-72 rounded-full bg-[rgba(44,120,251,0.12)] blur-3xl" />
        <div className="absolute bottom-0 right-[-4rem] h-80 w-80 rounded-full bg-[rgba(62,224,190,0.16)] blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="section-eyebrow mb-6">
            <Icon name="CurrencyDollarIcon" size={18} className="text-[var(--primary)]" />
            <span>Nuestra Filosofía de Precios</span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6">
            Acceso{' '}
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'var(--gradient-app)' }}>
              Gratuito para Siempre
            </span>
          </h2>

          <p className="text-xl text-gray-600 leading-relaxed">
            Creemos que el coaching empresarial de calidad debe ser accesible para todos los emprendedores, sin importar su presupuesto.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Free Forever */}
          <div className="app-card p-8 border border-[rgba(62,224,190,0.28)] hover:border-[rgba(62,224,190,0.48)] transition-all duration-300">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg" style={{ backgroundImage: 'linear-gradient(135deg, #3ee0be 0%, #2c78fb 100%)' }}>
                <Icon name="CheckBadgeIcon" size={24} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Versión Gratuita</h3>
            </div>
            <p className="text-lg font-semibold text-[var(--primary)] mb-4">Gratis para Siempre</p>
            <p className="text-gray-600 leading-relaxed mb-6">
              Nuestra versión gratuita permanecerá disponible indefinidamente. Incluye acceso a los mentores y funcionalidades activas hoy, tokens semanales y el núcleo esencial para impulsar tu negocio.
            </p>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Icon name="CheckCircleIcon" size={20} className="text-[var(--primary)] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Acceso a los mentores activos hoy y al roadmap público de especialidades IA</span>
              </li>
              <li className="flex items-start space-x-3">
                <Icon name="CheckCircleIcon" size={20} className="text-[var(--primary)] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">40 tokens iniciales al crear tu cuenta</span>
              </li>
              <li className="flex items-start space-x-3">
                <Icon name="CheckCircleIcon" size={20} className="text-[var(--primary)] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">10 tokens semanales los lunes si te quedan 5 o menos</span>
              </li>
              <li className="flex items-start space-x-3">
                <Icon name="CheckCircleIcon" size={20} className="text-[var(--primary)] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Todas las funcionalidades activas hoy dentro de la plataforma</span>
              </li>
              <li className="flex items-start space-x-3">
                <Icon name="CheckCircleIcon" size={20} className="text-[var(--primary)] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Historial de sesiones</span>
              </li>
              <li className="flex items-start space-x-3">
                <Icon name="CheckCircleIcon" size={20} className="text-[var(--primary)] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Ideal para 1 diagnóstico potente o 3 sesiones ligeras de ideación</span>
              </li>
            </ul>
          </div>

          {/* Premium Recommended */}
          <div className="app-card p-8 border border-[rgba(44,120,251,0.22)] hover:border-[rgba(44,120,251,0.42)] transition-all duration-300 relative overflow-hidden">
            <div className="absolute top-4 right-4">
              <span className="inline-block px-3 py-1 text-white text-xs font-bold rounded-full shadow-lg" style={{ backgroundImage: 'var(--gradient-primary-action)' }}>RECOMENDADO</span>
            </div>
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg" style={{ backgroundImage: 'var(--gradient-primary-action)' }}>
                <Icon name="SparklesIcon" size={24} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Versión Premium</h3>
            </div>
            <p className="text-lg font-semibold text-[var(--primary-strong)] mb-4">Plan LynMet activo hoy</p>
            <p className="text-gray-600 leading-relaxed mb-6">
              Para emprendedores que necesitan más continuidad, sesiones más seguidas, prioridad sobre nuevas releases especializadas y la tranquilidad de poder recargar cuando el ritmo del negocio lo pide.
            </p>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Icon name="StarIcon" size={20} className="text-[var(--primary-strong)] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">100 tokens mensuales</span>
              </li>
              <li className="flex items-start space-x-3">
                <Icon name="StarIcon" size={20} className="text-[var(--primary-strong)] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">20 tokens semanales los lunes si te quedan 5 o menos</span>
              </li>
              <li className="flex items-start space-x-3">
                <Icon name="StarIcon" size={20} className="text-[var(--primary-strong)] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">La recarga premium reemplaza la gratuita; no se suman ambas</span>
              </li>
              <li className="flex items-start space-x-3">
                <Icon name="StarIcon" size={20} className="text-[var(--primary-strong)] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Misma plataforma completa, con menos fricción para seguir avanzando</span>
              </li>
              <li className="flex items-start space-x-3">
                <Icon name="StarIcon" size={20} className="text-[var(--primary-strong)] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Primer acceso a nuevas mentorías especialistas a medida que se vayan liberando</span>
              </li>
              <li className="flex items-start space-x-3">
                <Icon name="StarIcon" size={20} className="text-[var(--primary-strong)] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Ideal para 3 diagnósticos o combinar diagnóstico + exploración entre mentores</span>
              </li>
            </ul>
            <Link
              href="/pricing-page"
              className="btn-primary-app mt-8 inline-flex items-center justify-center gap-2 px-5 py-3"
            >
              <span>Ver planes</span>
              <Icon name="ArrowRightIcon" size={18} />
            </Link>
          </div>
        </div>

        {/* Removed pricing page link - page is currently deactivated */}
      </div>
    </section>
  );
};

export default PricingPhilosophySection;
